/**
 * Dashboard.tsx
 *
 * Overview page: summary cards + analytics charts.
 * The full document table lives on the "All Documents" page (/items).
 */

import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";

import Header from "../components/Header";
import SummaryCard from "../components/SummaryCard";
import DashboardCharts from "../components/DashboardCharts";
import ItemModal from "../components/ItemModal";
import ImportModal from "../components/ImportModal";
import SettingsModal from "../components/SettingsModal";

import { useDocumentPage } from "../hooks/useDocumentPage";

import { buildSummaryCards } from "../utils/expiryUtils";

// ─── Component ────────────────────────────────────────────────────────────────

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    items,
    isLoading,
    isModalOpen,
    editingItem,
    modalRenewalMode,
    modalFocusExpiry,
    closeModal,
    handleCreate,
    handleRenewItem,
    handleSendReminder,
    handleSubmit,
    handleImport,
  } = useDocumentPage();

  // ── Modal state ───────────────────────────────────────────────────────────
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Track any pending deep-link action from reminder email buttons
  const pendingAction = useRef<{
    action: string;
    itemId: string;
    days?: number;
  } | null>(null);

  // ── Read URL params (import shortcut or email deep-link) ──────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // ?import=1 — chat-widget "Import CSV" shortcut
    if (params.get("import") === "1") {
      setIsImportOpen(true);
      navigate("/dashboard", { replace: true });
      return;
    }

    // ?action= + ?itemId= — reminder email deep-link buttons
    const action = params.get("action");
    const itemId = params.get("itemId") ?? "";
    const days = parseInt(params.get("days") ?? "30", 10);

    if (action && itemId) {
      pendingAction.current = { action, itemId, days };
      navigate("/dashboard", { replace: true });
    }
  }, [location.search, navigate]);

  // ── Dispatch deep-link action once items are loaded ───────────────────────
  useEffect(() => {
    if (!pendingAction.current || isLoading || items.length === 0) return;

    const { action, itemId, days } = pendingAction.current;
    pendingAction.current = null;

    const found = items.find((it) => it.id === itemId);
    if (!found) {
      toast.error(`Document #${itemId} not found.`);
      return;
    }

    if (action === "renew") {
      handleRenewItem(found);
    } else if (action === "snooze") {
      toast.success(
        `⏰ Reminders snoozed for ${days} day${days !== 1 ? "s" : ""} on "${found.name}".`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, isLoading]);

  // ── Summary card data ──────────────────────────────────────────────────────
  const summaryCards = useMemo(() => buildSummaryCards(items), [items]);

  // ── Loading state ──────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <>
        <Header />
        <div className="bg-[#F9FAFB] min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2563EB]" />
        </div>
      </>
    );
  }

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <>
      <Header
        onAddExpiry={handleCreate}
        onImport={() => setIsImportOpen(true)}
        onOpenPricing={() => navigate("/pricing")}
        onSettings={() => setIsSettingsOpen(true)}
      />

      <div className="bg-[#F9FAFB] min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* ── Analytics Charts ──────────────────────────────────────────── */}
          <DashboardCharts items={items} />
        </div>
      </div>

      {/* ── Item Modal ─────────────────────────────────────────────────────── */}
      {isModalOpen && (
        <ItemModal
          item={editingItem}
          onClose={closeModal}
          onSubmit={handleSubmit}
          initialRenewalMode={modalRenewalMode}
          focusExpiryDate={modalFocusExpiry}
        />
      )}

      {/* ── Import Modal ───────────────────────────────────────────────────── */}
      {isImportOpen && (
        <ImportModal
          onClose={() => setIsImportOpen(false)}
          onImport={async (csvText) => {
            await handleImport(csvText);
            setIsImportOpen(false);
          }}
        />
      )}

      {/* ── Settings Modal ─────────────────────────────────────────────────── */}
      {isSettingsOpen && (
        <SettingsModal onClose={() => setIsSettingsOpen(false)} />
      )}
    </>
  );
};

export default Dashboard;
